/**
 * unlockAudio — workaround for restrictive in-app browsers (Instagram,
 * Facebook, Messenger, TikTok, LinkedIn webviews) that block audio playback
 * unless it is triggered synchronously by a direct user gesture.
 *
 * Modes like Respiration Guidée and Scan Corporel play voice prompts on a
 * timer (setInterval / requestAnimationFrame), which the webview considers
 * "autoplay" and silently drops. By playing a tiny silent sound at the same
 * synchronous tick as the user's tap, we register a user-initiated play and
 * unlock the AudioContext for subsequent timer-driven sounds.
 *
 * MUST be called synchronously from the click/touch handler (before any
 * setState or async operation).
 */
import { Howl, Howler } from 'howler';

// 1-frame, silent WAV (44 bytes header + 2 samples of silence).
// Inlined as data URL so no network round-trip is needed during the gesture.
const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
export { SILENT_WAV };

let unlocked = false;
let silentHowl = null;

export function unlockAudio() {
  // Already unlocked in this session — no-op for performance.
  if (unlocked) return;
  try {
    // Resume the shared Howler AudioContext if the browser suspended it.
    if (Howler && Howler.ctx && Howler.ctx.state === 'suspended') {
      Howler.ctx.resume();
    }
    if (!silentHowl) {
      silentHowl = new Howl({
        src: [SILENT_WAV],
        volume: 0.01,
        html5: false, // use Web Audio path so Howler.ctx is engaged
        preload: true,
      });
    }
    // Play synchronously. Howler will queue if not yet loaded, but the user
    // gesture is registered immediately on this call.
    silentHowl.play();
    unlocked = true;
  } catch {
    // Best-effort: never throw from a click handler.
  }
}

export default unlockAudio;
